import * as React from 'react';
import { cn } from '../../lib/utils';

export interface PlatformTopbarProps
  extends React.HTMLAttributes<HTMLElement> {
  /** Content rendered on the left edge (logo, breadcrumb, mobile-nav trigger). */
  leading?: React.ReactNode;
  /** Center cluster (typically a global search trigger). Optional. */
  center?: React.ReactNode;
  /** Right-aligned cluster (indicators, switchers, notifications, user menu). */
  trailing?: React.ReactNode;
  /** Drop the bottom border when stacking under another sticky surface. */
  borderless?: boolean;
}

/**
 * PlatformTopbar — the sticky 60px header shared across every ZAFROI app.
 *
 * Slot-driven: `leading` / `center` / `trailing`. If `children` is supplied,
 * it replaces the default three-section layout entirely.
 */
export const PlatformTopbar = React.forwardRef<
  HTMLElement,
  PlatformTopbarProps
>(
  (
    { leading, center, trailing, borderless, className, children, ...props },
    ref,
  ) => {
    return (
      <header
        ref={ref}
        className={cn(
          'sticky top-0 z-30 flex h-[72px] w-full items-center gap-3 bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/70 md:px-6',
          !borderless && 'border-b border-border',
          className,
        )}
        {...props}
      >
        {children ?? (
          <>
            <div className="flex min-w-0 items-center gap-3">{leading}</div>
            <div className="flex min-w-0 flex-1 items-center justify-center">
              {center}
            </div>
            <div className="flex items-center gap-1.5">{trailing}</div>
          </>
        )}
      </header>
    );
  },
);
PlatformTopbar.displayName = 'PlatformTopbar';
